'use client';

import { useState, useEffect, useMemo } from 'react';
import { tagService } from '@/lib/tag-service';

interface AssetGlossaryStatus {
  // key format: "token:SYMBOL" or "nft:slug" → the glossary tag's slug (the
  // dossier door), or null when the asset has no glossary page.
  [key: string]: string | null;
}

/**
 * Hook to resolve which assets have glossary pages — and WHICH page: the value is
 * the glossary tag's slug, so consumers can door straight to `/tags/<slug>/`
 * instead of bouncing through the legacy `/coins/*` → `/tags/*` redirect chain.
 */
export function useAssetGlossaryStatus(
  tokens: Array<{ symbol: string }>,
  nfts: Array<{ slug: string }>
) {
  const [glossaryStatus, setGlossaryStatus] = useState<AssetGlossaryStatus>({});
  const [loading, setLoading] = useState(true);

  // Create stable keys for dependency tracking
  const tokenKey = useMemo(() => tokens.map(t => t.symbol).sort().join(','), [tokens]);
  const nftKey = useMemo(() => nfts.map(n => n.slug).sort().join(','), [nfts]);

  useEffect(() => {
    const fetchGlossaryStatus = async () => {
      setLoading(true);
      const status: AssetGlossaryStatus = {};

      try {
        // Fetch glossary status for tokens
        const tokenPromises = tokens.map(async (token) => {
          const tags = await tagService.getTagsByLinkedAsset('token', token.symbol);
          const glossaryTag = tags.find(tag => tag.hasGlossaryPage === true);
          status[`token:${token.symbol}`] = glossaryTag?.slug ?? null;
        });

        // Fetch glossary status for NFTs
        const nftPromises = nfts.map(async (nft) => {
          const tags = await tagService.getTagsByLinkedAsset('nft', nft.slug);
          const glossaryTag = tags.find(tag => tag.hasGlossaryPage === true);
          status[`nft:${nft.slug}`] = glossaryTag?.slug ?? null;
        });

        await Promise.all([...tokenPromises, ...nftPromises]);
        setGlossaryStatus(status);
      } catch (error) {
        console.error('Error fetching glossary status:', error);
      } finally {
        setLoading(false);
      }
    };

    if (tokens.length > 0 || nfts.length > 0) {
      fetchGlossaryStatus();
    } else {
      setLoading(false);
    }
  }, [tokenKey, nftKey, tokens, nfts]);

  return { glossaryStatus, loading };
}

